// src/hooks/useUserInitialization.js

import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useAuthToken } from './useAuthToken';
import { apiConfig } from '../config/auth.config';

/**
 * Hook to automatically initialize user in database on first login
 * Checks if user exists and creates if necessary
 */
export const useUserInitialization = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth0();
  const { getToken } = useAuthToken();
  const [isInitializing, setIsInitializing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    const initializeUser = async () => {
      // Don't run if not authenticated or still loading
      if (!isAuthenticated || authLoading || isInitialized || isInitializing) {
        return;
      }

      setIsInitializing(true);
      setInitError(null);

      try {
        const token = await getToken();

        // Try to get user API key - if it fails, user doesn't exist
        try {
          await axios.get(`${apiConfig.baseUrl}/get_api_key`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          // User exists
          setIsInitialized(true);
        } catch (error) {
          // User doesn't exist (404 or other error), create them
          if (error.response?.status === 404 || error.response?.status === 500) {
            console.log('User not found in database, creating...');
            
            await axios.post(
              `${apiConfig.baseUrl}/create_user`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            console.log('User created successfully');
            setIsInitialized(true);
          } else {
            throw error;
          }
        }
      } catch (error) {
        console.error('Error initializing user:', error);
        setInitError(error.message || 'Failed to initialize user');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeUser();
  }, [isAuthenticated, authLoading, getToken, isInitialized, isInitializing]);

  return { isInitializing, isInitialized, initError };
};
